import { takeLatest, takeEvery } from 'redux-saga';
import { put, call, fork, select, take } from 'redux-saga/effects';
import { fromJS } from 'immutable';

import { extractAssetURLData } from '../../utils/asset-url-helper-service';
import { getUserId, getUserName } from '../../selectors/user-selectors';
import { getDiscussionId, getDiscussionName, getDeliverableName, getMessageListQuery, getCurrentBrief, getCurrentContract, getSignedContract } from '../../selectors/discussion-selectors';
import { getDiscussionIsLoaded, getDiscussionUnreadMessageCount } from '../../selectors/discussion-list-selectors';
import { getCampaignId, getCampaignName, getPermissionId, getTeamDiscussionId, getPartnerAgencyDiscussionId, getClientDiscussionId } from '../../selectors/campaign-selectors';
import { getIsMyCampaignPermission } from '../../selectors/campaign-permission-selectors';
import { sendBirdService } from '../../utils/sendbird-service';
import { updateDocumentApproved, getDocument, removeDocument } from '../../utils/jsonapi-functions/documents';
import { getDiscussion, getReceiveMessageDiscussion, updateBudgetServer } from '../../utils/jsonapi-functions/discussions';
import { getDeliverable } from '../../utils/jsonapi-functions/deliverables';
import { BRIEF, CONTRACT, SIGNED_CONTRACT, DISCUSSION_PAGE, CAMPAIGN } from '../../utils/static-configuration';

import { checkDesktopNotificationPermissions, sendDesktopNotification } from '../../utils/notifications-service';
import { loadAuthToken } from '../../sagas/auth-token';
import { getSendbirdChannel, getSendbirdChannelURL, getSendbirdChannelLoading } from '../../selectors/sendbird-selectors';
import { checkSendbirdUserIsSetup, loadSendbirdChannel } from '../../sagas/sendbird';
import { reportError } from '../../utils/error-handling-service';
import { trackEvent } from '../../utils/metric-analytics-service';
import { ADD_TRACKED_ASSET } from '../../containers/track-asset-modal/constants';
import { LOAD_CAMPAIGN, LOAD_CAMPAIGN_SUCCESS } from '../campaign/constants';
import { SET_UNREAD_MESSAGE_COUNT } from '../discussion-list/constants';
import { SET_SENDBIRD_CHANNEL_URL, LOAD_SENDBIRD_CHANNEL_SUCCESS, SET_MESSAGE_LIST_QUERY } from '../../constants/sendbird';
import {
  CLEAR_DISCUSSION,
  LOAD_DISCUSSION,
  SET_DISCUSSION,
  LOAD_DISCUSSION_ERROR,
  LOAD_SENDBIRD_MESSAGES,
  SEND_MESSAGE,
  RECEIVE_MESSAGE,
  UPDATE_MESSAGES,
  LOAD_MORE_MESSAGES,
  UPDATE_DOCUMENTS,
  UPDATE_DELIVERABLES,
  NEW_DOCUMENT,
  UNAPPROVE_DOCUMENT,
  APPROVE_DOCUMENT,
  REMOVE_DOCUMENT,
  UPDATE_DELIVERABLE_APPROVED,
  UPDATE_DELIVERABLE_APPROVED_SUCCESS,
  UPDATE_DELIVERABLE_APPROVED_ERROR,
  SET_DISCUSSION_TYPE,
  UPDATE_CURRENT_BRIEF,
  UPDATE_CURRENT_CONTRACT,
  UPDATE_SIGNED_CONTRACT,
  UPDATE_DELIVERABLE_DOCUMENT,
  UPDATE_TRACKED_ASSETS,
  UPDATE_BUDGET,
  UPDATE_BUDGET_SUCCESS,
  LOAD_DISCUSSION_SUCCESS,
} from './constants';

const Scroll = require('react-scroll');

const scroll = Scroll.animateScroll;


export function* sendNewSendbirdMessage(action) {
  // Ensure user is logged into sendbird
  yield call(checkSendbirdUserIsSetup);

  // Ensure the sendbird channel is loaded
  let channel = yield select(getSendbirdChannel);

  if (!channel) {
    yield call(loadSendbirdChannel);
    yield take(LOAD_SENDBIRD_CHANNEL_SUCCESS);
    channel = yield select(getSendbirdChannel);
  }

  const { message, metaData, page } = action.data;

  try {
    if (channel) {
      const messageResponse = yield call(sendBirdService.sendSendbirdMessage, channel, message, metaData);
      yield put({ type: UPDATE_MESSAGES, message: fromJS([messageResponse]) });

      if (metaData.messageType === 'message') {
        const campaignId = yield select(getCampaignId);
        const discussionName = yield select(getDiscussionName);

        const sendMessageData = {
          campaignId,
          collaborationId: metaData.discussionId,
          collaborationName: discussionName,
        };
        trackEvent('Send Message', sendMessageData);
      }

      if (page === DISCUSSION_PAGE) scroll.scrollToBottom({ duration: 0 });
    }
  } catch (error) {
    reportError('SEND_SENDBIRD_MESSAGE_ERROR', error.message);
  }
}

export function* loadMoreMessages() {
  const channel = yield select(getSendbirdChannel);
  const messageListQuery = yield select(getMessageListQuery);

  if (channel && messageListQuery) {
    const messages = yield call(sendBirdService.loadSendbirdMessages, messageListQuery, channel);

    yield put({ type: LOAD_SENDBIRD_MESSAGES, messages: fromJS(messages) });
  }
}

function getBriefStatus(isBriefActive, isBriefApproved) {
  if (!isBriefActive && !isBriefApproved) return 'incomplete';
  if (isBriefActive && !isBriefApproved) return 'pending';
  if (isBriefActive && isBriefApproved) return 'complete';
}

function getContractStatus(isContractActive, isSignedContractActive) {
  if (!isContractActive) return 'incomplete';
  if (isContractActive && !isSignedContractActive) return 'pending';
  if (isContractActive && isSignedContractActive) return 'complete';
}

function getDeliverableStatus(first, second, third) {
  let status = 'incomplete';
  if (first < second) status = 'pending';
  if (first === second) status = 'complete';
  if (third) {
    if (first === second && first !== third) status = 'pending';
  }
  return status;
}

function getDeliverablesInfo(deliverables) {
  const deliverablesStatus = {};
  if (deliverables.size === 0) return deliverablesStatus;

  let setDeliverables = 0;
  let addedDeliverables = 0;
  let approvedDeliverables = 0;
  let liveDeliverables = 0;

  deliverables.forEach(deliverable => {
    setDeliverables++;
    if (deliverable.get('document')) {
      addedDeliverables++;
      if (deliverable.getIn(['document', 'approved'])) approvedDeliverables++;
    }
    if (deliverable.get('live')) liveDeliverables++;
  });
  deliverablesStatus.set = setDeliverables;
  deliverablesStatus.added = addedDeliverables;
  deliverablesStatus.approved = approvedDeliverables;
  deliverablesStatus.live = liveDeliverables;
  deliverablesStatus.setStatus = getDeliverableStatus(liveDeliverables, setDeliverables);
  deliverablesStatus.addedStatus = getDeliverableStatus(addedDeliverables, setDeliverables);
  deliverablesStatus.approvedStatus = getDeliverableStatus(approvedDeliverables, addedDeliverables, setDeliverables);
  deliverablesStatus.liveStatus = getDeliverableStatus(liveDeliverables, approvedDeliverables);

  return deliverablesStatus;
}

export function* loadDiscussion(action) {
  yield call(loadAuthToken);

  const discussionId = action.discussionId;
  const receiveMessageCallback = action.receiveMessageCallback;

  try {
    const discussion = yield call(getDiscussion, discussionId);
    const channelURL = discussion.channelURL;

    yield put({ type: SET_SENDBIRD_CHANNEL_URL, sendbirdChannelURL: channelURL });

    const viewCollaborationData = {
      campaignId: discussion.campaign.id,
      collaborationId: discussionId,
      collaborationName: discussion.name,
    };
    trackEvent('View Collaboration', viewCollaborationData);

    // Effects will get executed in parallel
    yield [
      call(loadSendbirdData, discussionId, channelURL, receiveMessageCallback),
      call(formatDiscussionData, discussion),
    ];

    yield put({ type: LOAD_DISCUSSION_SUCCESS });
  } catch (error) {
    reportError('LOAD_DISCUSSION_DATA_ERROR', error.message);
    yield put({ type: LOAD_DISCUSSION_ERROR, error: error.message });
  }
}

export function* loadSendbirdMessages() {
  try {
    yield call(loadSendbirdChannel);

    const isSendbirdChannelLoading = yield select(getSendbirdChannelLoading);
    if (isSendbirdChannelLoading) yield take(LOAD_SENDBIRD_CHANNEL_SUCCESS);
    const channel = yield select(getSendbirdChannel);

    const messageListQuery = channel.createPreviousMessageListQuery();
    yield put({ type: SET_MESSAGE_LIST_QUERY, messageListQuery });

    const messages = yield call(sendBirdService.loadSendbirdMessages, messageListQuery, channel);
    yield put({ type: LOAD_SENDBIRD_MESSAGES, messages: fromJS(messages) });

    scroll.scrollToBottom({ duration: 0 });
  } catch (error) {
    reportError('LOAD_SENDBIRD_MESSAGES_ERROR', error.message);
    yield put({ type: LOAD_DISCUSSION_ERROR, error: error.message });
  }
}

export function* markMessagesAsRead(discussionId, channelURL, userId) {
  try {
    const options = {
      sendbirdApiToken: process.env.REACT_APP_SENDBIRD_API_TOKEN,
      channelURL,
      userId,
    };


    const discussionLoaded = yield select(getDiscussionIsLoaded, discussionId);
    if (discussionLoaded) {
      yield put({
        type: SET_UNREAD_MESSAGE_COUNT,
        discussionId,
        unreadMessageCount: 0,
      });
    }
  } catch (error) {
    reportError('MARK_MESSAGE_AS_READ_ERROR', error.message);
    yield put({ type: LOAD_DISCUSSION_ERROR, error: error.message });
  }
}

export function addMessageReceivedChannelHandler(discussionId, receiveMessageCallback) {
  const sendBird = sendBirdService.getSendbirdService();
  const ChannelHandler = new sendBird.ChannelHandler();

  ChannelHandler.onMessageReceived = function (channel, message) {
    receiveMessageCallback(message);
    scroll.scrollToBottom({ duration: 0 });
  };

  sendBird.addChannelHandler(discussionId, ChannelHandler);
}

export function* loadSendbirdData(discussionId, channelURL, receiveMessageCallback) {
  try {
    // Ensure user is logged into sendbird
    yield call(checkSendbirdUserIsSetup);

    const userId = yield select(getUserId);

    // Effects will get executed in parallel
    yield [
      call(loadSendbirdMessages, channelURL),
      call(markMessagesAsRead, discussionId, channelURL, userId),
      call(addMessageReceivedChannelHandler, discussionId, receiveMessageCallback),
    ];
  } catch (error) {
    reportError('LOAD_SENDBIRD_DATA_ERROR', error.message);
    yield put({ type: LOAD_DISCUSSION_ERROR, error: error.message });
  }
}

export function* formatDiscussionData(discussion) {
  try {
    const discussionId = discussion.id;
    const name = discussion.name;
    const campaignId = discussion.campaign ? discussion.campaign.id : null;
    const channelURL = discussion.channelURL;
    const currentBrief = discussion.currentBrief;
    const currentContract = discussion.currentContract;
    const signedContract = discussion.signedContract;
    const trackedAssets = discussion.trackedAssets;
    const budget = discussion.budget;
    const budgetStatus = discussion.budgetStatus;
    const discussionType = discussion.discussionType;

    const documents = {};
    discussion.documents.forEach(documentFile => {
      if (!documentFile) return;
      documents[documentFile.id] = documentFile;
    });
    const immutableDocuments = fromJS(documents);

    const deliverables = {};
    discussion.deliverables.forEach(deliverable => {
      if (!deliverable) return;
      deliverables[deliverable.id] = deliverable;
      const deliverableDocuments = {};
      let highestVersionDocument = null;
      let currentHighestVersion = 0;
      if (deliverable.documents && deliverable.documents[0]) {
        deliverable.documents.forEach(document => {
          deliverableDocuments[document.version] = document;
          if (document.version > currentHighestVersion) {
            highestVersionDocument = document;
            currentHighestVersion = document.version;
          }
        });
      }
      deliverable.documents = deliverableDocuments;
      deliverable.mostRecentDocument = highestVersionDocument || deliverable.document;
    });
    const immutableDeliverables = fromJS(deliverables);

    const organisations = {};
    discussion.organisations.forEach(organisation => {
      if (!organisation) return;
      organisations[organisation.id] = organisation;
    });
    const immutableOrganisations = fromJS(organisations);

    const currentCampaignId = yield select(getCampaignId);

    if (campaignId && campaignId !== currentCampaignId) {
      yield put({ type: LOAD_CAMPAIGN, campaignId });
      yield take(LOAD_CAMPAIGN_SUCCESS);
    }

    const teamDiscussionId = yield select(getTeamDiscussionId);
    const clientDiscussionId = yield select(getClientDiscussionId);
    const partnerAgencyDiscussionId = yield select(getPartnerAgencyDiscussionId);

    if (discussionId === teamDiscussionId || discussionId === clientDiscussionId || discussionId === partnerAgencyDiscussionId) {
      yield put({ type: SET_DISCUSSION_TYPE, discussionType: 'chat' });
    } else {
      yield put({ type: SET_DISCUSSION_TYPE, discussionType: 'collaboration' });
    }

    // Work out documents status
    const isBriefActive = !!(currentBrief && !currentBrief.removed);
    const isBriefApproved = !!(currentBrief && currentBrief.approved);
    const isContractActive = !!(currentContract && !currentContract.removed);
    const isSignedContractActive = !!(signedContract && !signedContract.removed);

    const docsStatus = {
      brief: getBriefStatus(isBriefActive, isBriefApproved),
      contract: getContractStatus(isContractActive, isSignedContractActive),
    };
    const deliverablesStatus = getDeliverablesInfo(immutableDeliverables);

    const trackedAssetsList = {};
    trackedAssets && trackedAssets.forEach(trackedAsset => {
      if (!trackedAsset) return;
      trackedAssetsList[trackedAsset.assetId] = trackedAsset;
    });
    const immutableTrackedAssets = trackedAssets && trackedAssets[0] ? fromJS(trackedAssetsList) : fromJS({});

    yield put({
      type: SET_DISCUSSION,
      documents: immutableDocuments,
      deliverables: immutableDeliverables,
      discussionId,
      name,
      organisations: immutableOrganisations,
      currentBrief,
      currentContract,
      signedContract,
      docsStatus,
      deliverablesStatus,
      trackedAssets: immutableTrackedAssets,
      budget,
      budgetStatus,
      discussionType,
    });
  } catch (error) {
    reportError('FORMAT_DISCUSSION_DATA_ERROR', error.message);
    yield put({ type: LOAD_DISCUSSION_ERROR, error: error.message });
  }
}

export function* updateDocuments(action) {
  const documentData = action.documentData;
  yield put({ type: UPDATE_DOCUMENTS, documentData: fromJS(documentData) });
}

export function* updateBudget(action) {
  const { budget, status } = action;
  const d = yield select(getDiscussionId);
  try {
    yield call(updateBudgetServer, budget, status, d);
    yield put({
      type: UPDATE_BUDGET_SUCCESS,
      budget,
      status,
    });
  } catch (error) {
    reportError('UPDATE_BUDGET_ERROR', error.message);
  }
}

export function* approveDocument(action) {
  const documentId = action.documentId;
  const approvedData = action.approvedData;

  try {
    const newDocumentJSON = yield call(updateDocumentApproved, documentId, approvedData);

    const permissionId = yield select(getPermissionId);
    const campaignId = yield select(getCampaignId);
    const discussionId = yield select(getDiscussionId);
    const discussionName = yield select(getDiscussionName);
    const messageType = newDocumentJSON.docType.toLowerCase();
    const message = messageType;
    const metaData = {
      messageType,
      discussionId,
      permissionId,
      docId: newDocumentJSON.id,
      docStatus: 'approved',
    };

    if (newDocumentJSON.docType === BRIEF) {
      const approveDocumentData = {
        campaignId,
        collaborationId: discussionId,
        collaborationName: discussionName,
        docId: newDocumentJSON.id,
      };
      trackEvent('Approve Brief', approveDocumentData);
    }

    const messageData = {
      message,
      metaData,
      page: DISCUSSION_PAGE,
    };
    yield put({ type: SEND_MESSAGE, data: messageData });

    yield put({
      type: UPDATE_DOCUMENTS,
      documentData: fromJS(newDocumentJSON),
    });
  } catch (error) {
    reportError('APPROVE_DOCUMENT_ERROR', error.message);
  }
}

export function* unapproveDocument(action) {
  const documentId = action.documentId;
  const docType = action.docType;
  const messageType = docType.toLowerCase();

  try {
    const permissionId = yield select(getPermissionId);
    const discussionId = yield select(getDiscussionId);
    const message = messageType;
    const metaData = {
      messageType,
      discussionId,
      permissionId,
      docId: documentId,
      docStatus: 'removed',
    };

    yield call(removeDocument, documentId);

    const messageData = {
      message,
      metaData,
      page: DISCUSSION_PAGE,
    };
    yield put({ type: SEND_MESSAGE, data: messageData });

    // TODO Could remove document out of state
    yield put({ type: REMOVE_DOCUMENT, documentId });
  } catch (error) {
    reportError('UNAPPROVE_DOCUMENT_ERROR', error.message);
  }
}

export function* updateDeliverableApprovedStatus(action) {
  try {
    const { approved, documentId, deliverableId } = action.data;
    const userName = yield select(getUserName);

    // Swap approval
    const updatedApproved = !approved;
    const approvedData = {
      approved: updatedApproved,
      approvedBy: userName,
      approvalDate: new Date(),
    };

    const newDocument = yield call(updateDocumentApproved, documentId, approvedData);
    const immutableDocument = fromJS(newDocument);
    const permissionId = yield select(getPermissionId);
    const discussionId = yield select(getDiscussionId);
    const message = 'deliverable';
    const metaData = {
      messageType: 'deliverable',
      discussionId,
      permissionId,
      docId: newDocument.id,
      deliverableId,
      deliverableStatus: approvedData.approved ? 'approved' : 'submitted',
    };

    const campaignId = yield select(getCampaignId);
    const campaignName = yield select(getCampaignName);
    const discussionName = yield select(getDiscussionName);
    const deliverableName = yield select(getDeliverableName, deliverableId);

    const approveDeliverableData = {
      campaignId,
      collaborationId: discussionId,
      collaborationName: discussionName,
      deliverableId,
      deliverableName,
    };
    trackEvent('Approve Deliverable', approveDeliverableData);

    const messageData = {
      message,
      metaData,
      page: DISCUSSION_PAGE,
    };
    yield put({ type: SEND_MESSAGE, data: messageData });

    yield put({ type: UPDATE_DOCUMENTS, documentData: immutableDocument });
    yield put({ type: UPDATE_DELIVERABLE_DOCUMENT, deliverableId, documentData: immutableDocument });
    yield put({ type: UPDATE_DELIVERABLE_APPROVED_SUCCESS });
  } catch (error) {
    yield put({ type: UPDATE_DELIVERABLE_APPROVED_ERROR, error: error.message });
    reportError('UPDATE_DELIVERABLE_APPROVED_ERROR', error.message);
  }
}

export function* receiveMessage(action) {
  const messageObject = action.message;
  const data = JSON.parse(messageObject.data);

  const discussionId = data.discussionId;
  const currentChannelURL = yield select(getSendbirdChannelURL);
  const messageChannelURL = messageObject.channelUrl;
  const userId = yield select(getUserId);

  const isMyCampaignPermission = yield select(getIsMyCampaignPermission, data.permissionId);

  // If the user is currently in the same discussion then render the received message
  if (currentChannelURL === messageChannelURL) {
    try {
      const currentBrief = yield select(getCurrentBrief);
      const currentContract = yield select(getCurrentContract);
      const signedContract = yield select(getSignedContract);

      // If the message has a document then load it into state
      let documentDataJSON;
      if (data.docId) {
        const documentData = yield call(getDocument, data.docId);
        documentDataJSON = documentData.toJSONTree();

        if (documentDataJSON.docType === BRIEF && documentDataJSON.id !== currentBrief) {
          yield put({ type: UPDATE_CURRENT_BRIEF, currentBrief: documentDataJSON.id });
        }
        if (documentDataJSON.docType === CONTRACT && documentDataJSON.id !== currentContract) {
          yield put({ type: UPDATE_CURRENT_CONTRACT, currentContract: documentDataJSON.id });
        }
        if (documentDataJSON.docType === SIGNED_CONTRACT && documentDataJSON.id !== signedContract) {
          yield put({ type: UPDATE_SIGNED_CONTRACT, signedContract: documentDataJSON.id });
        }
        yield put({ type: UPDATE_DOCUMENTS, documentData: fromJS(documentDataJSON) });
      }

      // If the message has a deliverable then load it into state
      if (data.deliverableId) {
        const deliverableData = yield call(getDeliverable, data.deliverableId);
        if (documentDataJSON) deliverableData.mostRecentDocument = documentDataJSON;
        yield put({ type: UPDATE_DELIVERABLES, deliverableData: fromJS(deliverableData) });
        if (deliverableData.document) {
          yield put({ type: UPDATE_DOCUMENTS, documentData: fromJS(deliverableData.document) });
        }
      }

      const options = {
        sendbirdApiToken: process.env.REACT_APP_SENDBIRD_API_TOKEN,
        channelURL: currentChannelURL,
        userId,
      };

      if (data.messageType && data.messageType === 'budget') {
        const { budget, status } = data;
        yield put({ type: UPDATE_BUDGET, budget, status });
      }


      const discussionLoaded = yield select(getDiscussionIsLoaded, discussionId);
      if (discussionLoaded) yield put({ type: SET_UNREAD_MESSAGE_COUNT, discussionId, unreadMessageCount: 0 });

      // Finally update the message stream for the user
      yield put({ type: UPDATE_MESSAGES, message: messageObject });
      scroll.scrollToBottom({ duration: 0 });
    } catch (error) {
      reportError('RECEIVE_MESSAGE_ERROR', error.message);
    }
  } else {
    try {
      if (!isMyCampaignPermission) {
        const discussion = yield call(getReceiveMessageDiscussion, discussionId);
        const campaignId = yield select(getCampaignId);

        const unreadMessageCount = yield select(getDiscussionUnreadMessageCount, discussionId);
        if (campaignId === discussion.campaign.id) {
          yield put({ type: SET_UNREAD_MESSAGE_COUNT, discussionId, unreadMessageCount: unreadMessageCount + 1 });
        }

        if (checkDesktopNotificationPermissions()) {
          const notificationMessageOptions = {
            deliverable_added: 'Deliverable assigned:',
            deliverable_submitted: 'Deliverable added:',
            deliverable_approved: 'Deliverable approved:',
            brief_added: 'Brief added:',
            brief_approved: 'Brief approved:',
            contract_added: 'Contract added:',
            signed_contract_added: 'Contract signed:',
            document_added: 'Document added:',
          };

          const title = `${discussion.campaign.name}, ${discussion.name}`;
          let notificationMessage = messageObject.message;
          if (notificationMessageOptions[`${data.messageType}_${data.deliverableStatus}`]) {
            notificationMessage = `${notificationMessageOptions[`${data.messageType}_${data.deliverableStatus}`]} ${messageObject.message}`;
          } else if (notificationMessageOptions[`${data.messageType}_${data.docStatus}`]) {
            notificationMessage = `${notificationMessageOptions[`${data.messageType}_${data.docStatus}`]} ${messageObject.message}`;
          }

          const options = {
            body: notificationMessage,
            icon: '/small-dyzio-logomark.png',
          };

          sendDesktopNotification(title, options, openDiscussionCallback.bind(this, discussionId));
        }
      }
    } catch (error) {
      reportError('RECEIVE_NOTIFICATION_ERROR', error.message);
    }
  }
}

function openDiscussionCallback(discussionId) {
  const { origin } = window.location;
  window.open(`${origin}/campaign/discussion/${discussionId}`, '_blank');
}

export function* watchLoadMoreMessages() {
  yield takeLatest(LOAD_MORE_MESSAGES, loadMoreMessages);
}

export function* watchSendbirdMessages() {
  yield takeEvery(SEND_MESSAGE, sendNewSendbirdMessage);
}

export function* watchRecieveMessages() {
  yield takeEvery(RECEIVE_MESSAGE, receiveMessage);
}

export function* watchLoadDiscussion() {
  yield takeEvery(LOAD_DISCUSSION, loadDiscussion);
}

export function* watchUpdateDocument() {
  yield takeEvery(NEW_DOCUMENT, updateDocuments);
}

export function* watchUpdateBudget() {
  yield takeEvery(UPDATE_BUDGET, updateBudget);
}

export function* watchApproveDocument() {
  yield takeEvery(APPROVE_DOCUMENT, approveDocument);
}

export function* watchUnapproveDocument() {
  yield takeEvery(UNAPPROVE_DOCUMENT, unapproveDocument);
}

export function* watchUpdateDeliverableApproved() {
  yield takeEvery(UPDATE_DELIVERABLE_APPROVED, updateDeliverableApprovedStatus);
}

export default function* discussionsSagas() {
  yield [
    fork(watchSendbirdMessages),
    fork(watchRecieveMessages),
    fork(watchLoadMoreMessages),
    fork(watchLoadDiscussion),
    fork(watchUpdateDocument),
    fork(watchUnapproveDocument),
    fork(watchApproveDocument),
    fork(watchUpdateDeliverableApproved),
    fork(watchUpdateBudget),
  ];
}



/** WEBPACK FOOTER **
 ** ./src/containers/discussion/sagas.js
 **/